import { Button, CircularProgress, Divider, Grid, Typography, Box, Paper, IconButton, Avatar, Stack, Chip, Tooltip } from "@mui/material";
import { Close, StorageRounded, HistoryRounded, CodeRounded, ListRounded, SecurityRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";

// Resource Servers Details View Component
const ResourceServersDetail = ({ handleViewModalClose, serverId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();

    const { isLoading, data, isFetching, error } = useQuery(["resource_server_detail", serverId], () => {
        return axiosPrivate.get(`/resourceServers/${serverId}`);
    }, { refetchOnWindowFocus: false });

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", {
                variant: "error",
                autoHideDuration: 2000
            });
        }
    }, [enqueueSnackbar, error]);

    const server = data?.data?.data;

    return (
        <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '500px' },
                    borderRadius: { xs: '24px 24px 0 0', sm: 6 },
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
                    overflow: 'hidden',
                    position: 'relative',
                    maxHeight: '95vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <IconButton
                    onClick={handleViewModalClose}
                    sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled', zIndex: 1 }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ p: { xs: 3, sm: 4 }, overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: 'secondary.lighter',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.05)',
                                mb: 2,
                                borderRadius: 3
                            }}
                        >
                            <StorageRounded sx={{ fontSize: 40, color: 'secondary.main' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                            {server?.name}
                        </Typography>
                        <Chip
                            label="Resource Server"
                            size="small"
                            sx={{ mt: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', bgcolor: 'rgba(0,0,0,0.05)' }}
                        />
                    </Box>

                    {isLoading || isFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Stack spacing={3}>
                            <DetailSection title="Architecture Details">
                                <DetailItem icon={<CodeRounded fontSize="small" />} label="Service ID" value={server?.id} />
                                <DetailItem icon={<HistoryRounded fontSize="small" />} label="Registered On" value={moment(server?.created_at).format("LLLL")} />
                            </DetailSection>

                            <DetailSection title="Defined Permissions (Scopes)">
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {server?.scopes?.map((scope, idx) => (
                                        <Tooltip key={idx} title={scope.description}>
                                            <Chip
                                                label={scope.name}
                                                variant="outlined"
                                                size="small"
                                                sx={{ fontWeight: 700, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}
                                            />
                                        </Tooltip>
                                    ))}
                                    {(!server?.scopes || server?.scopes.length === 0) && (
                                        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No scopes defined for this server.</Typography>
                                    )}
                                </Box>
                            </DetailSection>
                        </Stack>
                    )}

                    <Box sx={{ mt: 4 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: 'secondary.main' }}
                            onClick={handleViewModalClose}
                        >
                            Close Details
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

function DetailSection({ title, children }) {
    return (
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1, display: 'block', letterSpacing: '1px' }}>
                {title}
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 4, border: '1px solid rgba(0,0,0,0.03)' }}>
                {children}
            </Paper>
        </Box>
    );
}

function DetailItem({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, '&:last-child': { mb: 0 } }}>
            <Box sx={{ color: 'primary.main', mt: 0.5, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main', wordBreak: 'break-all' }}>
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
}

export default ResourceServersDetail;
